import URI from 'urijs';
import { appQueryParameters, url as urlUtils } from '@clubmed/utils';
import type {
  ChildcareAgeInMonthsProps,
  ProductChildcareAvailabilitiesProps,
} from 'Components/widgets/product/Product.mapper';
import type { ProductComparisonProps } from 'Components/widgets/product/product-comparison/ProductComparison';
import type { ProductFlagProps } from 'Components/widgets/product/product-flag/ProductFlag';
import type { ProductHeaderProps } from 'Components/widgets/product/product-header/ProductHeader';
import type { OfferDiscountProps } from 'Components/widgets/offer-discount/OfferDiscount';
import type { Props as ProductThumbnailProps } from 'Components/widgets/product/product-thumbnail/ProductThumbnail';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { isStrictlyIntegrationEnv, isStrictlyStagingEnv } from 'Utils/context/contextUtils';
import { useGQL as runGQLQuery } from 'Utils/graphql-client/use-gql/useGQL';
import { LINE_CRUISE_EXCLUSIVITY } from 'Utils/request/requestDictionary';
export const grid: ScreenSizesProps = {
  mobile: 1,
  phablet: 2,
  tablet: 3,
  desktop: 3,
};
export type ProductProps = {
  childcare?: ProductChildcareAvailabilitiesProps;
  childcareAgeInMonths?: ChildcareAgeInMonthsProps;
  childcareIncluded?: boolean;
  className?: string;
  comfort?: number;
  comparison?: ProductComparisonProps;
  coordinates?: [number, number];
  departureDateOn?: string;
  flag?: ProductFlagProps;
  hasComparison?: boolean;
  hasDiscount?: boolean;
  hasFadeIn?: boolean;
  hasLazyLoading?: boolean;
  header: ProductHeaderProps;
  id: string;
  isCruise?: boolean;
  isLinkDisabled?: boolean;
  isTour?: boolean;
  kids?: Array<Kids>;
  minAuthorizedAge?: {
    value: number;
  };
  offerDiscount?: OfferDiscountProps;
  onClick?: (arg0: React.SyntheticEvent<HTMLElement>) => void;
  price?: ProductPrice | Record<string, any>;
  resortRegion?: string;
  thumbnail?: ProductThumbnailProps;
  readonly tripadvisor?: {
    rating: {
      basedOn: string;
      title: string;
    };
    review: {
      tripadvisorTraveler: string;
      reviewBy: string;
      reviewDate: string;
    };
    menubar: {
      awards: string;
      ratings: string;
      reviews: string;
    };
  };
  type: string;
  url?: string;
};
export type ctmaLinksParams = {
  cmtaIntegLink: string;
  cmtaStagingLink: string;
  cmtaProdLink: string;
};
export const generateProductLink = (url: string, offer?: Offer | null | undefined): string => {
  const newLink =
    offer && offer.seoName
      ? new URI(url)
          .addSearch(appQueryParameters.shopping.offer, urlUtils.normalizeSlug(offer.seoName))
          .toString()
      : url;
  return urlUtils.normalizeUrl(newLink);
};
export const generateLinkProp = (url?: string, offer?: Offer) => {
  return url
    ? {
        url: generateProductLink(url, offer),
        queryParamsToIgnore: offer ? [] : ['offer'],
      }
    : undefined;
};
export const localeHasCmtaLinks = (cmtaLinks: ctmaLinksParams) =>
  cmtaLinks && cmtaLinks.cmtaIntegLink && cmtaLinks.cmtaStagingLink && cmtaLinks.cmtaProdLink;
export const generateCmtaLinkProp = (resortId: string, cmtaLinks: ctmaLinksParams) => {
  const { cmtaIntegLink, cmtaStagingLink, cmtaProdLink } = cmtaLinks;
  let cmtaUrl = cmtaProdLink;

  if (isStrictlyIntegrationEnv()) {
    cmtaUrl = cmtaIntegLink;
  }

  if (isStrictlyStagingEnv()) {
    cmtaUrl = cmtaStagingLink;
  }

  return {
    url: cmtaUrl + resortId,
    isExternalLink: true,
  };
};
type GetCruiseExclusivity = {
  isCruiseExclusive: boolean;
  isCruiseExclusivityLoading: boolean;
};
export const getCruiseExclusivity = (productId: string = ''): GetCruiseExclusivity => {
  const { data, isLoading: isCruiseExclusivityLoading } = runGQLQuery({
    query: `query {
      cruiseExclusivity(productId: "${productId}") {
        isExclusive
      }
    }`,
    queryOptions: {
      requestName: LINE_CRUISE_EXCLUSIVITY,
    },
  });
  return {
    isCruiseExclusive: !!(data as { cruiseExclusivity?: { isExclusive?: boolean } })
      ?.cruiseExclusivity?.isExclusive,
    isCruiseExclusivityLoading,
  };
};
